import { Button, Checkbox, Flex } from '@vtex/admin-ui'
import { NumberInput, useFormState, useWatch } from '@vtex/admin-ui-form'
import { useCollectionsImport, useSkusImport } from 'hooks'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { LIMITS } from 'shared'
import {
  CancelButton,
  CollectionsSelector,
  CONNECTOR,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Rule,
  RuleContent,
  RuleStack,
  SkuSelector,
  UseDrawerStateReturn,
} from 'ui'
import { type BuyAndWinTrigger } from '../../../../../typings'
import styles from '../styles'
import { messages } from './messages'
import { suite } from './validation'
const { vestResolver } = require('@hookform/resolvers/vest')

type BuyAndWinTriggerEditorProps = {
  state: UseDrawerStateReturn
  initialConfig: BuyAndWinTrigger
  onSave: (config: BuyAndWinTrigger) => void
}

export function BuyAndWinTriggerEditor(props: BuyAndWinTriggerEditorProps) {
  const { state, initialConfig, onSave } = props
  const intl = useIntl()
  const { $t } = intl

  const form = useFormState<BuyAndWinTrigger>({
    defaultValues: initialConfig,
    resolver: vestResolver(suite(intl)),
  })

  useEffect(() => {
    state.open && form.reset({ ...initialConfig })
  }, [form, initialConfig, state.open])

  const [skus, collections, sameItemsOnly] = useWatch({
    form,
    //@ts-ignore: although useWatch is able to retrieve multiple fields, admin-ui-hooks does not expose it on its typings
    name: ['skus', 'collections', 'sameItemsOnly'],
  })

  const { importCollections, loading: importingCollections } =
    useCollectionsImport()
  const { importSkus, loading: importingSkus } = useSkusImport()

  const handleSave = async () => {
    const valid = await form.trigger()

    if (!valid) return

    onSave(form.getValues())

    state.hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.editor.title} />
        </DrawerTitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <div className={styles.editor.trigger.content}>
          <div className={styles.editor.trigger.scope}>
            <RuleStack>
              <Rule>
                <RuleContent fluid>
                  <SkuSelector>
                    <SkuSelector.Header>
                      <SkuSelector.Download values={skus} />
                      <SkuSelector.Upload
                        name="skus"
                        form={form}
                        importer={importSkus}
                        maxEntries={LIMITS.MAX_BUY_TOGETHER_SKUS}
                      />
                    </SkuSelector.Header>
                    <SkuSelector.Selector
                      name="skus"
                      state={form}
                      loading={importingSkus}
                      maxEntries={LIMITS.MAX_BUY_TOGETHER_SKUS}
                    />
                  </SkuSelector>
                </RuleContent>
              </Rule>

              <Rule connector={CONNECTOR.OR}>
                <RuleContent fluid>
                  <CollectionsSelector>
                    <CollectionsSelector.Header>
                      <CollectionsSelector.Download values={collections} />
                      <CollectionsSelector.Upload
                        form={form}
                        importer={importCollections}
                        name="collections"
                        maxEntries={LIMITS.MAX_BUY_TOGETHER_COLLECTIONS}
                      />
                    </CollectionsSelector.Header>

                    <CollectionsSelector.Selector
                      form={form}
                      name="collections"
                      loading={importingCollections}
                      maxEntries={LIMITS.MAX_BUY_TOGETHER_COLLECTIONS}
                    />
                  </CollectionsSelector>
                </RuleContent>
              </Rule>
            </RuleStack>

            <Checkbox
              label={$t(messages.editor.sameItemsOnly)}
              checked={sameItemsOnly}
              onChange={(event) =>
                form.setValue('sameItemsOnly', event.target.checked)
              }
            />
          </div>

          <Flex>
            <NumberInput
              name="minQuantity"
              state={form}
              label={$t(messages.editor.minQuantity)}
              // @ts-expect-error Property 'min' does not exist on type 'IntrinsicAttributes & InheritedProps$4 & FormFieldProps' (typings error)
              min={1}
            />
          </Flex>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <CancelButton onClick={state.hide} />
        <Button variant="primary" onClick={handleSave}>
          <FormattedMessage {...messages.apply} />
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
